import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X, ShoppingBag, Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import { getProductPhoto, getVialPhoto, getAccessoryPhoto, getBundlePhoto } from "@/lib/productPhotos";
import { lineKey } from "@/lib/cartKey";

// Cart flyout drawer. Opens automatically whenever addItem() runs. Shows the
// item that was just added, three complementary cross-sells, and two CTAs
// (keep shopping vs go to full cart). The full /cart page is still reachable
// via the header cart icon and the "View cart" button here, this drawer
// exists to bridge the add-to-cart moment with a cross-sell surface, not to
// replace the full cart page.
export default function CartDrawer() {
  const { items, flyoutOpen, closeFlyout, lastAddedKey, subtotal, addItem, addAddonToLine } = useCart();
  const { format: fmtPrice } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    if (!flyoutOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && closeFlyout();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [flyoutOpen, closeFlyout]);

  const lastLine = items.find((i) => lineKey(i) === lastAddedKey) || items[items.length - 1];

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products-all"],
    queryFn: async () => (await api.get("/products")).data,
    enabled: flyoutOpen,
  });

  // Live add-on config, used to price the Raw Vial → Recon Kit prompt
  // in the drawer. Falls back gracefully to null so the prompt just
  // doesn't render when the config is unreachable / disabled.
  const { data: addonsConfig } = useQuery({
    queryKey: ["site-settings-addons"],
    queryFn: async () => (await api.get("/site-settings/addons")).data,
    enabled: flyoutOpen,
    staleTime: 60 * 1000,
  });

  // Line-level modifiers offered in the drawer, keyed by the last-added
  // product's format. Same keys the PDP AddonToggle mounts, so config
  // (price/enabled/stock) always resolves against the SAME
  // `/site-settings/addons` document. Order in the array is the render
  // order in the drawer (Recon Kit first, Sharps Bin second on Raw Vial).
  const DRAWER_ADDONS_BY_FORMAT = {
    raw: [
      {
        key: "recon_kit",
        blurb: "BAC water + insulin syringes + alcohol wipes. Ships in the same box.",
      },
      {
        key: "sharps_bin",
        blurb: "Puncture-resistant container for used needles and syringes, BS7320:1990 compliant.",
      },
    ],
    pen: [
      {
        key: "full_kit",
        blurb: "Travel Case + Sharps Bin packaged together, same as adding both à la carte, £4.99 cheaper.",
      },
      {
        key: "travel_case",
        blurb: "Maintains 2–8°C cold chain during transit, pen + refill capacity.",
      },
      {
        key: "sharps_bin",
        blurb: "Puncture-resistant container for used needles and syringes, BS7320:1990 compliant.",
      },
    ],
  };

  // Build the render-ready list of add-on prompts for this line: drop
  // anything disabled, out of stock, or already carried on the line
  // (the buyer may have flipped the toggle on the PDP, don't
  // double-prompt them for the same modifier). Pen Kit also gets
  // Full-Kit-vs-individuals resolution: if the line has neither
  // travel_case nor sharps_bin, show ONLY Full Kit (packaged deal,
  // £4.99 cheaper); if either component is already on the line, hide
  // Full Kit (piecemeal discounting isn't offered) and fall back to
  // whichever individual is still missing.
  const drawerAddons = useMemo(() => {
    if (!lastLine || !addonsConfig) return [];
    const eligible = DRAWER_ADDONS_BY_FORMAT[lastLine.format] || [];
    const lineSkus = new Set((lastLine.addons || []).map((a) => a.sku));
    const fullKitCfg = addonsConfig.full_kit;
    const travelCfg = addonsConfig.travel_case;
    const sharpsCfg = addonsConfig.sharps_bin;
    // "Has any Full-Kit component", decides whether the Full Kit
    // upsell is still meaningful. If the buyer has already opted into
    // one component, don't tease them with a discount they can't
    // actually claim on this line.
    const hasFullKit = fullKitCfg && lineSkus.has(fullKitCfg.sku);
    const hasTravel = travelCfg && lineSkus.has(travelCfg.sku);
    const hasSharps = sharpsCfg && lineSkus.has(sharpsCfg.sku);
    return eligible
      .map(({ key, blurb }) => {
        const config = addonsConfig[key];
        if (!config || !config.enabled) return null;
        if ((config.stock ?? 0) <= 0) return null;
        if (lineSkus.has(config.sku)) return null;
        if (key === "full_kit") {
          // Full Kit only makes sense as a "everything at once" nudge,
          // suppress once the buyer has picked a component individually.
          if (hasTravel || hasSharps) return null;
        } else if (lastLine.format === "pen") {
          // Individual Pen Kit modifiers give way to Full Kit, hide
          // them when Full Kit is still on offer or already selected.
          const fullKitStillOffered =
            fullKitCfg && fullKitCfg.enabled && (fullKitCfg.stock ?? 0) > 0 && !hasTravel && !hasSharps && !hasFullKit;
          if (fullKitStillOffered) return null;
          if (hasFullKit) return null;
        }
        return { key, blurb, config };
      })
      .filter(Boolean);
  }, [lastLine, addonsConfig]);

  const addDrawerAddonToLastLine = (config) => {
    if (!config || !lastLine) return;
    addAddonToLine(lineKey(lastLine), {
      sku: config.sku,
      name: config.name,
      price: config.price,
    });
  };

  // Cross-sell logic: prefer products in the same category as the last-added
  // item, minus anything already in the cart. Falls back to best_sellers if
  // no category matches (e.g. accessories only).
  const suggestions = useMemo(() => {
    if (!lastLine || !allProducts.length) return [];
    const inCart = new Set(items.map((i) => i.slug));
    const meta = allProducts.find((p) => p.slug === lastLine.slug);
    const targetCats = new Set(meta?.categories || []);
    const targetFormat = meta?.format;
    const candidates = allProducts.filter(
      (p) => !inCart.has(p.slug) && !p.coming_soon && !p.hidden
    );
    const byCategory = candidates.filter((p) =>
      (p.categories || []).some((c) => targetCats.has(c))
    );
    const byFormat = candidates.filter((p) => p.format === targetFormat);
    const bestSellers = candidates.filter((p) => p.is_best_seller);
    const seen = new Set();
    const merged = [];
    for (const p of [...byCategory, ...byFormat, ...bestSellers]) {
      if (!seen.has(p.slug)) {
        seen.add(p.slug);
        merged.push(p);
      }
      if (merged.length >= 3) break;
    }
    return merged;
  }, [lastLine, allProducts, items]);

  const photoFor = (p) => {
    if (!p || !p.slug) return null;
    if (p.is_accessory) return getAccessoryPhoto(p.slug);
    if (p.format === "raw") return getVialPhoto(p.slug);
    return (
      getProductPhoto(p.slug, "SEALED") ||
      getProductPhoto(p.slug, "OPEN") ||
      getBundlePhoto(p.slug)
    );
  };

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity ${flyoutOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      data-testid="cart-drawer"
      aria-hidden={!flyoutOpen}
    >
      <div className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" onClick={closeFlyout} />
      <aside
        className={`absolute right-0 top-0 flex h-full w-[min(440px,92vw)] flex-col bg-white shadow-2xl transition-transform ${flyoutOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-brandblue" />
            <span className="font-mono text-[11px] tracking-[0.22em] text-subtle">ADDED TO CART</span>
          </div>
          <button
            onClick={closeFlyout}
            className="rounded-full p-1 text-ink hover:bg-line/60"
            aria-label="Close"
            data-testid="cart-drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {lastLine && (
            <div className="flex gap-4 rounded-xl panel-warm bg-white p-3" data-testid="cart-drawer-last-added">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#F5F8FC]">
                {(() => {
                  // Photo resolution order:
                  //   1. lastLine.image (bundles pass this when added to cart)
                  //   2. product photo mapper (SEALED/OPEN/PEN or raw vial)
                  //   3. Package icon fallback for anything without media
                  //      (bundles without photography ship, see BundleCard)
                  const p = allProducts.find((x) => x.slug === lastLine.slug)
                    || { slug: lastLine.slug, format: lastLine.format };
                  // Only trust local product-photo assets, the seeded
                  // unsplash placeholder on bundles.image is not
                  // representative of the actual bundle so we skip it in
                  // favour of the Package fallback for a cleaner look.
                  const localImage =
                    lastLine.image && lastLine.image.startsWith("/") ? lastLine.image : null;
                  const photo = localImage || photoFor(p);
                  return photo ? (
                    <img
                      src={photo}
                      alt={lastLine.name}
                      className="h-full w-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <Package className="h-6 w-6 text-brandblue/70" strokeWidth={1.5} aria-hidden="true" />
                  );
                })()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-bold text-ink line-clamp-2">{lastLine.name}</p>
                {(lastLine.addons || []).length > 0 && (
                  <ul className="mt-0.5 space-y-0.5" data-testid="cart-drawer-lastline-addons">
                    {lastLine.addons.map((a) => (
                      <li key={a.sku} className="font-mono text-[10px] tracking-[0.06em] text-brandbluedark">
                        + {a.name}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1 text-xs text-subtle">Qty {lastLine.quantity}</p>
                <p className="mt-0.5 font-heading text-sm font-black text-ink">
                  {fmtPrice(lastLine.price * lastLine.quantity, { fractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}

          {drawerAddons.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="cart-drawer-addon-prompts">
              <p className="label-mono">§ Add to this line</p>
              {drawerAddons.map(({ key, blurb, config }) => (
                <label
                  key={key}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-brandblue/30 bg-brandbluelight/60 p-3 transition-colors hover:border-brandblue"
                  data-testid={`cart-drawer-addon-${key}-wrap`}
                >
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => addDrawerAddonToLastLine(config)}
                    className="mt-1 h-4 w-4 shrink-0 accent-brandblue"
                    data-testid={`cart-drawer-addon-${key}-checkbox`}
                    aria-label={`Add ${config.name}`}
                  />
                  <span className="flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="font-heading text-sm font-bold text-ink">
                        Add {config.name}
                      </span>
                      <span
                        className="font-mono text-[12px] font-semibold text-brandbluedark"
                        data-testid={`cart-drawer-addon-${key}-price`}
                      >
                        + {fmtPrice(config.price)}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs text-subtle">{blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="mt-6" data-testid="cart-drawer-crosssells">
              <p className="label-mono">§ You might also like</p>
              <ul className="mt-3 space-y-2">
                {suggestions.map((p) => {
                  const photo = photoFor(p);
                  return (
                    <li
                      key={p.slug}
                      className="flex items-center gap-3 rounded-xl border border-line bg-white p-2 hover:border-brandblue/40"
                      data-testid={`cart-drawer-suggestion-${p.slug}`}
                    >
                      <Link
                        to={`/product/${p.slug}`}
                        onClick={closeFlyout}
                        className="flex flex-1 items-center gap-3 min-w-0"
                      >
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-[#F6F7F9]">
                          {photo && (
                            <img src={photo} alt={p.name} className="h-full w-full object-contain" draggable={false} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-heading text-[13px] font-bold text-ink">{p.name}</p>
                          <p className="text-xs text-subtle">{fmtPrice(p.price, { fractionDigits: 2 })}</p>
                        </div>
                      </Link>
                      <button
                        type="button"
                        onClick={() => addItem(p, 1)}
                        className="btn-pill bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navydark"
                        data-testid={`cart-drawer-add-${p.slug}`}
                      >
                        Add
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-line px-6 py-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-subtle">Cart subtotal</span>
            <span className="font-heading text-lg font-black text-ink" data-testid="cart-drawer-subtotal">
              {fmtPrice(subtotal, { fractionDigits: 2 })}
            </span>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                closeFlyout();
                navigate("/cart");
              }}
              className="btn-pill bg-brandblue px-5 py-3 text-sm font-bold text-white hover:bg-brandbluedark"
              data-testid="cart-drawer-view-cart"
            >
              View cart · checkout
            </button>
            <button
              type="button"
              onClick={closeFlyout}
              className="btn-pill btn-outline px-5 py-2.5 text-sm font-semibold"
              data-testid="cart-drawer-keep-shopping"
            >
              Keep shopping
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
